<script setup lang="ts">
/**
 * A meal's own details: its name, when, where, and when replies close.
 *
 * Saved whole. The API replaces every field on a save, so the dialog starts
 * from what the meal already has — including the reply deadline, turned back
 * into the meal's local date and time — and a field left alone stays as it was.
 *
 * Says what changed when it saves, because two of these are material (SPEC
 * §6.24.1): the date or time, and the place. The page decides whether to ask
 * about telling anybody; this only reports.
 */
import { computed, ref, watch } from 'vue'
import { ApiError, endpoints, type MealEventResponse } from '@/api'
import { localParts } from '@/utils/dates'

const props = defineProps<{
  modelValue: boolean
  groupId: string
  meal: MealEventResponse
}>()

const emit = defineEmits<{
  'update:modelValue': [open: boolean]
  saved: [meal: MealEventResponse, changes: string[]]
}>()

const title = ref('')
const date = ref('')
const time = ref('')
const locationName = ref('')
const locationAddress = ref('')
const notes = ref('')
const deadlineDate = ref('')
const deadlineTime = ref('')

const saving = ref(false)
const error = ref<string | null>(null)

function reset(): void {
  title.value = props.meal.title
  date.value = props.meal.localDate
  time.value = props.meal.localTime.slice(0, 5)
  locationName.value = props.meal.locationName ?? ''
  locationAddress.value = props.meal.locationAddress ?? ''
  notes.value = props.meal.notes ?? ''

  const deadline = props.meal.rsvpDeadlineUtc
    ? localParts(props.meal.rsvpDeadlineUtc, props.meal.timeZoneId)
    : null
  deadlineDate.value = deadline?.date ?? ''
  deadlineTime.value = deadline?.time ?? ''
  error.value = null
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) reset()
  },
  { immediate: true },
)

const valid = computed(() => title.value.trim() !== '' && date.value !== '' && time.value !== '')

/** Which material things this save would change (SPEC §6.24.1). */
function changesFrom(before: MealEventResponse, after: MealEventResponse): string[] {
  const changes: string[] = []

  if (before.localDate !== after.localDate || before.localTime !== after.localTime) {
    changes.push('time')
  }

  if (
    (before.locationName ?? '') !== (after.locationName ?? '') ||
    (before.locationAddress ?? '') !== (after.locationAddress ?? '')
  ) {
    changes.push('place')
  }

  return changes
}

async function save(): Promise<void> {
  saving.value = true
  error.value = null

  try {
    const updated = await endpoints.updateMealEvent(props.groupId, props.meal.id, {
      title: title.value.trim(),
      startDate: date.value,
      startTime: time.value,
      locationName: locationName.value.trim() || null,
      locationAddress: locationAddress.value.trim() || null,
      notes: notes.value.trim() || null,
      rsvpDeadlineDate: deadlineDate.value || null,
      rsvpDeadlineTime: deadlineDate.value ? deadlineTime.value || null : null,
      mealTemplateId: props.meal.mealTemplateId,
    })

    emit('saved', updated, changesFrom(props.meal, updated))
    emit('update:modelValue', false)
  } catch (caught) {
    error.value =
      (caught instanceof ApiError &&
        (Object.values(caught.problem?.errors ?? {})[0]?.[0] ?? caught.problem?.title)) ||
      'That did not save. Try again in a moment.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="32rem"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title tag="h2">Meal details</v-card-title>
      <v-card-text class="details">
        <v-text-field v-model="title" label="Name" name="title" density="comfortable" />
        <div class="details__pair">
          <v-text-field v-model="date" type="date" label="Date" name="date" density="comfortable" />
          <v-text-field v-model="time" type="time" label="Time" name="time" density="comfortable" />
        </div>
        <v-text-field
          v-model="locationName"
          label="Where"
          name="locationName"
          density="comfortable"
          hint="A place name, like Grandma's house"
        />
        <v-text-field
          v-model="locationAddress"
          label="Address"
          name="locationAddress"
          density="comfortable"
        />
        <div class="details__pair">
          <v-text-field
            v-model="deadlineDate"
            type="date"
            label="Replies close"
            name="deadlineDate"
            density="comfortable"
            hint="Leave empty to keep them open"
            persistent-hint
          />
          <v-text-field
            v-model="deadlineTime"
            type="time"
            label="At"
            name="deadlineTime"
            density="comfortable"
            :disabled="!deadlineDate"
          />
        </div>
        <v-textarea v-model="notes" label="Notes" name="notes" rows="2" auto-grow />

        <p v-if="error" class="details__error" role="alert">{{ error }}</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="emit('update:modelValue', false)">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!valid"
          data-testid="details-save"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.details__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.details__error {
  margin: 0;
  color: rgb(var(--v-theme-error));
}
</style>
